import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "sonner";
import { blockStudentsByPlan } from "../../../api/notes";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";

type RestrictionByPlanModalProps = {
  show: boolean;
  onClose: () => void;
  selectedYearId?: string;
  allPaymentPlans: any[];
  rawStudentPaymentData: any[];
  onApplied?: (updatedStudents?: any[]) => void;
  currency?: string;
};

const RestrictionByPlanModal = ({
  show,
  onClose,
  selectedYearId,
  allPaymentPlans,
  rawStudentPaymentData,
  onApplied,
  currency = "FCFA",
}: RestrictionByPlanModalProps) => {
  const [restrictionPlanId, setRestrictionPlanId] = useState("");
  const [restrictionClassIds, setRestrictionClassIds] = useState<string[]>([]);
  const [minimumRestAmount, setMinimumRestAmount] = useState<string>("");
  const [submittingRestriction, setSubmittingRestriction] = useState(false);

  const formatAmount = (amount: number) =>
    `${Number(amount || 0).toLocaleString("fr-FR")} ${currency}`;

  const hasSelectedPlan = (studentData: any, planId: string) => {
    if (!planId) return false;
    return (studentData?.plan || []).some(
      (plan: any) => plan?.PaymentPlan?.id === planId,
    );
  };

  const selectedRestrictionPlan = useMemo(
    () => allPaymentPlans.find((plan: any) => plan.id === restrictionPlanId),
    [allPaymentPlans, restrictionPlanId],
  );

  const maxMinimumRest = Number(selectedRestrictionPlan?.amount || 0);

  const restrictionClassOptions = useMemo(() => {
    const classMap = new Map<string, any>();

    rawStudentPaymentData.forEach((studentData: any) => {
      if (!hasSelectedPlan(studentData, restrictionPlanId)) return;
      const classId = String(studentData?.id_class || "");
      const classLabel = String(studentData?.classe || "");
      if (!classId || !classLabel) return;
      if (!classMap.has(classId)) {
        classMap.set(classId, {
          id: classId,
          label: classLabel,
          levelId: String(studentData?.id_level || studentData?.level_abbreviation || "without-level"),
          levelLabel: String(studentData?.level_abbreviation || "Sans niveau"),
        });
      }
    });

    return Array.from(classMap.values())
      .sort((a, b) => a.label.localeCompare(b.label, "fr"));
  }, [rawStudentPaymentData, restrictionPlanId]);

  useEffect(() => {
    if (!show) return;
    setRestrictionPlanId("");
    setRestrictionClassIds([]);
    setMinimumRestAmount("");
  }, [show]);

  useEffect(() => {
    setRestrictionClassIds([]);
    if (!restrictionPlanId) {
      setMinimumRestAmount("");
      return;
    }
    // remplire le plan avec le montant maximum
    // setMinimumRestAmount(String(maxMinimumRest));

    // remplire le plan avec le montant minimum
    setMinimumRestAmount(String(0));
  }, [restrictionPlanId, maxMinimumRest]);

  useEffect(() => {
    setRestrictionClassIds((prev) =>
      prev.filter((classId) =>
        restrictionClassOptions.some((option) => option.id === classId),
      ),
    );
  }, [restrictionClassOptions]);

  const toggleRestrictionClass = (classId: string) => {
    setRestrictionClassIds((prev) =>
      prev.includes(classId)
        ? prev.filter((item) => item !== classId)
        : [...prev, classId],
    );
  };

  const groupedClasses = useMemo(() => {
    const groups = new Map<string, { label: string; classes: any[] }>();
    restrictionClassOptions.forEach((option) => {
      const group: { label: string; classes: any[] } = groups.get(option.levelId) || {
        label: option.levelLabel,
        classes: [],
      };
      group.classes.push(option);
      groups.set(option.levelId, group);
    });
    return Array.from(groups.entries()).map(([id, group]) => ({ id, ...group }));
  }, [restrictionClassOptions]);

  const toggleAllClasses = () => {
    const allIds = restrictionClassOptions.map((option) => option.id);
    setRestrictionClassIds((current) => current.length === allIds.length ? [] : allIds);
  };

  const toggleLevel = (classIds: string[]) => {
    const allSelected = classIds.every((id) => restrictionClassIds.includes(id));
    setRestrictionClassIds((current) => allSelected
      ? current.filter((id) => !classIds.includes(id))
      : Array.from(new Set([...current, ...classIds])));
  };

  const closeModal = () => {
    if (submittingRestriction) return;
    onClose();
  };

  const applyRestrictionByPlan = async () => {
    if (!selectedYearId) {
      toast.warning("Aucune annee scolaire selectionnee.");
      return;
    }
    if (!restrictionPlanId) {
      toast.warning("Selectionnez un plan de paiement.");
      return;
    }
    if (restrictionClassIds.length === 0) {
      toast.warning("Selectionnez au moins une classe.");
      return;
    }

    const minAmount = Number(minimumRestAmount);
    if (Number.isNaN(minAmount)) {
      toast.warning("Le reste minimum est invalide.");
      return;
    }
    if (minAmount < 0 || minAmount > maxMinimumRest) {
      toast.warning(
        `Le reste minimum doit etre compris entre 0 et ${maxMinimumRest}.`,
      );
      return;
    }

    setSubmittingRestriction(true);
    try {
      const response = await blockStudentsByPlan({
        id_academicYear: selectedYearId,
        ids_plans: restrictionPlanId,
        ids_classes: restrictionClassIds,
        minAmount,
      });
      toast.success("Restriction des notes appliquee avec succes.");
      onClose();
      onApplied?.(response?.data || []);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Erreur lors de l'application de la restriction.",
      );
    } finally {
      setSubmittingRestriction(false);
    }
  };

  return (
    <Modal show={show} onHide={closeModal} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Blocage / Autorisation des notes par plan de paiement
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="row row-cols-xxl-5 row-cols-md-6">
          <div className="col-xxl-6 col-xl-6 col-md-6">
            <div className="mb-3">
              <label className="form-label fw-semibold">Plan de paiement</label>
              <select
                className="form-select"
                value={restrictionPlanId}
                onChange={(e) => setRestrictionPlanId(e.target.value)}
              >
                <option value="">Selectionner un plan</option>
                {allPaymentPlans.map((plan: any) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.label} - {formatAmount(Number(plan.amount || 0))}
                  </option>
                ))}
              </select>
              {restrictionPlanId && (
                <small className="text-muted">
                  Montant du plan selectionne: {formatAmount(maxMinimumRest)}
                </small>
              )}
            </div>
          </div>
          <div className="col-xxl-6 col-xl-6 col-md-6">
            <div className="mb-3">
              <label className="form-label fw-semibold">Reste minimum</label>
              <input
                type="number"
                min={0}
                max={maxMinimumRest}
                step="1"
                className="form-control"
                disabled={!restrictionPlanId}
                value={minimumRestAmount}
                onChange={(e) => setMinimumRestAmount(e.target.value)}
                placeholder="Entrer le reste minimum"
              />
              <small className="text-muted">
                Valeur attendue entre 0 et {formatAmount(maxMinimumRest)}.
              </small>
            </div>
          </div>
          <div className="col-xxl-12 col-xl-12 col-md-12">
            <div>
              <label className="form-label fw-semibold">
                Classes du plan sélectionné
              </label>
              {!restrictionPlanId ? (
                <div className="alert alert-light border mb-0">
                  Selectionnez d'abord un plan de paiement.
                </div>
              ) : restrictionClassOptions.length === 0 ? (
                <div className="alert alert-light border mb-0">
                  Aucune classe disponible pour ce plan.
                </div>
              ) : (
                <div className="dropdown w-100">
                  <button
                    className="btn btn-outline-light bg-white dropdown-toggle w-100"
                    data-bs-toggle="dropdown"
                    style={{ minWidth: "220px" }}
                  >
                    <i className="ti ti-wallet me-2" />
                    {restrictionClassIds?.length > 0
                      ? `${restrictionClassIds.length} classes(s)`
                      : "Classes de ce plan"}
                  </button>

                  <ul
                    className="dropdown-menu p-2 w-100"
                    onClick={(e) => e.stopPropagation()}
                    style={{ minWidth: "240px" }}
                  >
                    <li>
                      <label className="d-flex align-items-center gap-2 p-2 border-bottom fw-semibold" style={{ cursor: "pointer" }}>
                        <input type="checkbox" className="form-check-input m-0" checked={restrictionClassOptions.length > 0 && restrictionClassIds.length === restrictionClassOptions.length} onChange={toggleAllClasses} />
                        <span>Toutes les classes</span>
                      </label>
                    </li>
                    {groupedClasses.map((group) => {
                      const groupIds = group.classes.map((item) => item.id);
                      const selectedCount = groupIds.filter((id) => restrictionClassIds.includes(id)).length;
                      return (
                        <li key={group.id} className="border-bottom">
                          <label className="d-flex align-items-center gap-2 p-2 fw-semibold bg-light" style={{ cursor: "pointer" }}>
                            <input
                              type="checkbox"
                              className="form-check-input m-0"
                              checked={selectedCount === groupIds.length}
                              ref={(element) => { if (element) element.indeterminate = selectedCount > 0 && selectedCount < groupIds.length; }}
                              onChange={() => toggleLevel(groupIds)}
                            />
                            <span>{group.label}</span><span className="badge bg-white text-dark ms-auto">{selectedCount}/{groupIds.length}</span>
                          </label>
                          {group.classes.map((classOption) => (
                            <label key={classOption.id} className="d-flex align-items-center gap-2 py-2 px-4" style={{ cursor: "pointer" }}>
                              <input type="checkbox" className="form-check-input m-0" checked={restrictionClassIds.includes(classOption.id)} onChange={() => toggleRestrictionClass(classOption.id)} />
                              <span>{classOption.label}</span>
                            </label>
                          ))}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          className="btn btn-outline-secondary me-2"
          onClick={closeModal}
          disabled={submittingRestriction}
        >
          Annuler
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={applyRestrictionByPlan}
          disabled={submittingRestriction}
        >
          {submittingRestriction ? "Traitement..." : "Appliquer"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default withPermission(
  RestrictionByPlanModal,
  [
    ...Object.values(Action).map((action) =>
      buildPermission(Resource.NOTE_RESTRICTION, action),
    ),
  ],
  "masquer",
);
